/**
 * pastoralEthicsLayer.js
 * Detects sensitive pastoral situations and returns behavioral flags
 * that shape how Kevia responds — tone, limits, referral recommendations.
 *
 * Risk levels (ascending):
 *   none → low → medium → high → critical
 */

// ── Risk category patterns ────────────────────────────────────────────

const RISK_PATTERNS = {
  // CRITICAL — immediate safety concern
  suicide: [
    /suicid/i,
    /quitarme\s+la\s+vida/i,
    /hacerme\s+daño/i,
    /no\s+quiero\s+(?:seguir\s+)?vivir/i,
    /terminar\s+con\s+(?:todo|mi\s+vida)/i,
    /pensamientos\s+de\s+(?:muerte|suicidio)/i,
    /desaparecer\s+para\s+siempre/i,
    /mejor\s+estar\s+muerto/i,
  ],
  self_harm: [
    /me\s+(?:corto|lastimo|hago\s+daño)/i,
    /autolesion/i,
  ],

  // HIGH — serious situation requiring care + possible professional referral
  abuse: [
    /abuso\s+(?:físico|sexual|emocional|doméstico)/i,
    /violencia\s+(?:doméstica|familiar|de\s+género)/i,
    /me\s+(?:golpea|pega|maltrata|lastima|amenaza)/i,
    /maltrato/i,
  ],
  mental_health: [
    /depresi[oó]n\s+(?:severa|cl[ií]nica|profunda|muy)/i,
    /crisis\s+de\s+ansiedad\s+(?:severa|frecuente)/i,
    /trastorno/i,
    /internado\s+(?:por|en)\s+(?:hospital|clínica)/i,
    /medicaci[oó]n\s+(?:psiqui[aá]trica|para\s+la\s+mente)/i,
  ],
  addiction: [
    /adicci[oó]n/i,
    /alcoh[oó]lismo|alcoh[oó]lico/i,
    /drogadicci[oó]n|drogadicto/i,
    /pornograf[ií]a.*problema|adicto.*pornograf[ií]a/i,
    /juego.*compulsivo|ludopatía/i,
  ],

  // MEDIUM — sensitive pastoral situation requiring caution
  discipline: [
    /disciplinar\s+a\s+(?:un\s+)?miembro/i,
    /excluir\s+de\s+la\s+iglesia/i,
    /proceso\s+de\s+disciplina/i,
    /expulsar/i,
    /disciplina\s+eclesial/i,
  ],
  marital_crisis: [
    /infidelidad/i,
    /traici[oó]n\s+(?:matrimonial|conyugal)/i,
    /divorcio/i,
    /separaci[oó]n\s+(?:matrimonial|de\s+mi\s+esposo|de\s+mi\s+esposa)/i,
    /crisis\s+matrimonial/i,
  ],
  apostasy: [
    /apostasía/i,
    /abandon[oó]\s+la\s+(?:fe|iglesia)/i,
    /ya\s+no\s+(?:cree|quiere\s+saber\s+de\s+dios)/i,
    /dejó\s+de\s+creer/i,
    /renunci[oó]\s+a\s+la\s+fe/i,
  ],
  minors: [
    /menor\s+de\s+edad/i,
    /ni[ñn][oa]s?\s+(?:en\s+riesgo|involucrado)/i,
    /adolescente\s+(?:en\s+crisis|involucrado)/i,
    /proteger\s+a\s+(?:un\s+)?menor/i,
  ],

  // LOW — requires soft tone but not high caution
  general_pastoral_difficulty: [
    /situaci[oó]n\s+(?:muy\s+)?dif[ií]cil/i,
    /no\s+s[eé]\s+c[oó]mo\s+(?:ayudar|manejar)/i,
    /me\s+preocupa\s+(?:mucho|demasiado)/i,
    /conflicto\s+(?:pastoral|ministerial)/i,
  ],
};

// Escalation recommendations per category
const ESCALATION_NOTES = {
  suicide:     "Recomienda línea de crisis o ayuda de emergencia inmediatamente.",
  self_harm:   "Orienta hacia profesional de salud mental con urgencia.",
  abuse:       "Recomienda buscar ayuda profesional y/o autoridades competentes.",
  mental_health: "Orienta hacia apoyo psicológico o psiquiátrico especializado.",
  addiction:   "Sugiere considerar apoyo profesional especializado en adicciones.",
  discipline:  "Recomienda revisión con liderazgo y Manual de Iglesia.",
  minors:      "Prioriza protección del menor. Recomienda protocolos de protección.",
};

const RISK_ORDER = { none: 0, low: 1, medium: 2, high: 3, critical: 4 };

/**
 * @param {string} text
 * @param {string} intent
 * @param {{ dominantEmotion: string | null }} [emotionalState]
 * @returns {{
 *   riskLevel: "none" | "low" | "medium" | "high" | "critical",
 *   detected: string[],
 *   requiresProfessionalReferral: boolean,
 *   requiresSoftTone: boolean,
 *   requiresDoctrinalCaution: boolean,
 *   avoidDirectiveLanguage: boolean,
 *   escalationRecommendation: string | null,
 * }}
 */
export function detectPastoralRisk(text, intent, emotionalState) {
  if (!text?.trim()) return _emptyRisk();

  const detected = [];
  let riskLevel  = "none";

  for (const [category, patterns] of Object.entries(RISK_PATTERNS)) {
    if (patterns.some((p) => p.test(text))) {
      detected.push(category);
    }
  }

  // Determine highest risk level from detected categories
  const CATEGORY_RISK = {
    suicide:                   "critical",
    self_harm:                 "critical",
    abuse:                     "high",
    mental_health:             "high",
    addiction:                 "high",
    minors:                    "high",
    discipline:                "medium",
    marital_crisis:            "medium",
    apostasy:                  "medium",
    general_pastoral_difficulty: "low",
  };

  for (const cat of detected) {
    const catRisk = CATEGORY_RISK[cat] ?? "low";
    if (RISK_ORDER[catRisk] > RISK_ORDER[riskLevel]) {
      riskLevel = catRisk;
    }
  }

  // Bump risk if emotional distress is high (intensity signal from detector)
  if (emotionalState?.intensity === "high" && RISK_ORDER[riskLevel] < RISK_ORDER.medium) {
    riskLevel = "medium";
  }

  // Intent-based baseline
  if (intent === "prayer" && RISK_ORDER[riskLevel] < RISK_ORDER.low) {
    riskLevel = "low"; // prayer always merits soft tone
  }

  const isMediumPlus = RISK_ORDER[riskLevel] >= RISK_ORDER.medium;
  const isHighPlus   = RISK_ORDER[riskLevel] >= RISK_ORDER.high;

  return {
    riskLevel,
    detected,
    requiresProfessionalReferral: isHighPlus,
    requiresSoftTone:             RISK_ORDER[riskLevel] >= RISK_ORDER.low,
    requiresDoctrinalCaution:     detected.includes("discipline") || detected.includes("apostasy"),
    avoidDirectiveLanguage:       isMediumPlus,
    escalationRecommendation:     _buildEscalation(detected),
  };
}

function _buildEscalation(detected) {
  // Return the highest-priority escalation note
  const PRIORITY = ["suicide", "self_harm", "abuse", "minors", "mental_health", "addiction", "discipline"];
  for (const cat of PRIORITY) {
    if (detected.includes(cat) && ESCALATION_NOTES[cat]) {
      return ESCALATION_NOTES[cat];
    }
  }
  return null;
}

function _emptyRisk() {
  return {
    riskLevel:                    "none",
    detected:                     [],
    requiresProfessionalReferral: false,
    requiresSoftTone:             false,
    requiresDoctrinalCaution:     false,
    avoidDirectiveLanguage:       false,
    escalationRecommendation:     null,
  };
}
